import { useTranslation } from 'react-i18next';

import type {
  MultiLanguage,
  MultiSizeImage,
  TokenCampaignResponse,
} from '@/types/campaign';
import { CampaignType } from '@/types/campaign-type';

const campaignLanguages = ['nl', 'en', 'fr', 'de'] as const;
type CampaignLanguage = keyof MultiLanguage;

type CampaignFields = {
  landingTitle: string;
  landingTitleAriaLabel: string;
  landingPrimaryButtonTitle: string;
  successTitle: string;
  successTitleAriaLabel: string;
  landingImage?: MultiSizeImage;
};

function isCampaignLanguage(
  language: string | undefined
): language is CampaignLanguage {
  return campaignLanguages.some(
    (supportedLanguage) => supportedLanguage === language
  );
}

function resolveCampaignLanguage(language: string): CampaignLanguage {
  const baseLanguage = language.toLowerCase().split(/[-_]/)[0];

  return isCampaignLanguage(baseLanguage) ? baseLanguage : 'en';
}

export function useCampaignFields(
  campaign?: TokenCampaignResponse
): CampaignFields {
  const { t, i18n } = useTranslation(['translation', 'a11y']);
  const language = resolveCampaignLanguage(
    i18n.resolvedLanguage ?? i18n.language
  );
  const webFlowData =
    campaign?.type === CampaignType.TOKEN
      ? campaign.campaignWebFlowData
      : undefined;

  const localizedLandingTitle = webFlowData?.landingTitle?.[language]?.trim();
  const localizedButtonTitle =
    webFlowData?.landingPrimaryButtonTitle?.[language]?.trim();
  const localizedSuccessTitle = webFlowData?.successTitle?.[language]?.trim();

  return {
    landingTitle: localizedLandingTitle || t('landing.getYourDeposit'),
    landingTitleAriaLabel: localizedLandingTitle
      ? `${localizedLandingTitle} ${t('imgs.tikkieLogo')}`
      : t('a11y:landing.getYourDeposit'),
    landingPrimaryButtonTitle:
      localizedButtonTitle || t('landing.depositButton'),
    successTitle: localizedSuccessTitle || t('success.title'),
    successTitleAriaLabel: localizedSuccessTitle || t('a11y:success.title'),
    landingImage: webFlowData?.landingImage,
  };
}
